/**
 * BPAFindingsDataGrid
 *
 * Grid component for the BPA Findings page.
 * Delegates configuration and data-fetching to `useBPAFindingsGridConfig`,
 * keeping this component purely presentational.
 *
 * Error state: shown as an inline alert so the user can see the problem
 * without losing the filter/header chrome.
 */

import DataGrid from 'components/DataGrid';
import LoadingScreen from 'components/LoadingScreen';
import { useAtomValue } from 'jotai';
import React, { Suspense, useCallback, useState } from 'react';
import { companyIdAtom } from 'views/state';

import BPAFindingAgeHistoryModal from './BPAFindingAgeHistoryModal';
import { useBPAFindingsGridConfig } from './BPAFindingsDataGrid.config';
import BPANotesModal from './BPANotesModal';
import { BPAFinding } from '../../types/bpaFindings.types';

const BPAFindingsDataGrid: React.FC = () => {
  const companyId = useAtomValue(companyIdAtom);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [notesData, setNotesData] = useState({
    id: 0,
    title: '',
    haveUnreadNotes: false,
  });
  const [ageHistoryData, setAgeHistoryData] = useState<{
    findingId: number;
    findingTitle: string;
  } | null>(null);

  const openNotesModal = useCallback((rowData: BPAFinding) => {
    setNotesData({
      id: rowData.id,
      title: rowData.description || rowData.control_description || 'Finding',
      haveUnreadNotes: rowData.total_notes > 0 && rowData.unread_notes > 0,
    });
    setIsNotesModalOpen(true);
  }, []);

  const openAgeHistoryModal = useCallback((rowData: BPAFinding) => {
    setAgeHistoryData({
      findingId: rowData.id,
      findingTitle:
        rowData.description || rowData.control_description || 'Finding',
    });
  }, []);

  const { config, data, loading } = useBPAFindingsGridConfig(
    openNotesModal,
    openAgeHistoryModal,
  );

  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        <DataGrid config={config} data={data} loading={loading} />
      </Suspense>
      {isNotesModalOpen && (
        <BPANotesModal
          toggle={() => setIsNotesModalOpen(false)}
          findingId={notesData.id}
          title={notesData.title}
          haveUnreadNotes={notesData.haveUnreadNotes}
        />
      )}
      {ageHistoryData && (
        <BPAFindingAgeHistoryModal
          companyId={companyId}
          findingId={ageHistoryData.findingId}
          findingTitle={ageHistoryData.findingTitle}
          onClose={() => setAgeHistoryData(null)}
        />
      )}
    </>
  );
};

export default BPAFindingsDataGrid;
